/**
 * One-time cookie info bar (issue #74).
 *
 * NOT a consent banner. Per the #74 audit
 * (`docs/compliance/audit-74-75.md`), we only set strictly-necessary
 * cookies (Clerk auth session, Cloudflare `__cf_bm`) and use
 * localStorage exclusively for auth tokens + UX preferences. Under
 * both PIPA Art. 30 §1 ¶7 and ePrivacy Art. 5(3), strictly-necessary-
 * only sites do not require consent collection — only disclosure,
 * which lives in PrivacyPolicy.tsx §10-2.
 *
 * Adding "Accept all / Reject all" buttons would be incoherent here:
 * "Reject all" with only essential cookies either does nothing
 * (deceptive) or breaks login (defeats the user's expectation). So
 * this component renders an *informational* strip with a single
 * Dismiss action — defensible, honest, and gives a visible signal
 * to users / school-procurement reviewers that we've thought about
 * cookie hygiene.
 *
 * Toggle:
 *   • Env: VITE_COOKIE_NOTICE_ENABLED — default ON. Set to "0" /
 *     "false" to disable globally (e.g. once legal counsel has
 *     reviewed and decided we don't need any visible notice).
 *   • Per-user: localStorage `cookieNoticeDismissed` (set by the
 *     Dismiss button) — survives reloads, per-device.
 *
 * Route exceptions — the notice does NOT render on:
 *   • /privacy — covers the very page it links to.
 *   • /student/notice — covers the PIPA Art. 22-2 §3 plain-language
 *     notice's acknowledgment button. The notice card has its
 *     "이해했어요" action button bottom-aligned, and this strip's
 *     ``position: fixed; bottom: 56px`` overlays the same band.
 *     On the legally-required under-14 notice screen, the cookie
 *     info strip is the wrong visual priority anyway — the user
 *     is being shown a plain-language summary and a single action;
 *     layering an unrelated cookie strip on top dilutes both. The
 *     E2E ``seedSiteAccess`` helper pre-seeds the dismissed key as
 *     a defense-in-depth test-side mirror of this exception.
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useLang } from '../i18n'
import s from './CookieNotice.module.css'

const STORAGE_KEY = 'cookieNoticeDismissed'

function isEnabledByEnv(): boolean {
  // Vite exposes VITE_*-prefixed env vars on import.meta.env. Anything
  // not "0" or "false" (case-insensitive) is treated as enabled — and
  // an unset / empty value defaults to enabled per #74's "default-on"
  // decision.
  const raw = import.meta.env.VITE_COOKIE_NOTICE_ENABLED
  if (raw === undefined || raw === '') return true
  const v = String(raw).toLowerCase()
  return v !== '0' && v !== 'false'
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    // localStorage can throw in private-mode Safari + some embedded
    // browsers. If we can't read it, treat as not-dismissed and let
    // the user dismiss again — better than silently hiding the bar.
    return false
  }
}

function setDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, '1')
  } catch {
    // Same fallback as above — if write fails, the bar will reappear
    // on next mount, which is annoying but harmless.
  }
}

export default function CookieNotice() {
  const location = useLocation()
  const lang = useLang()
  // Track dismiss in component state so the click hides the bar
  // immediately (without waiting for a remount). Initial value comes
  // from the lazy-init function — runs once on first render, reads
  // env + localStorage there instead of in a mount effect (which
  // would trip react-hooks/set-state-in-effect under 7.1.1, and is
  // strictly worse anyway: a flash of the visible bar would render
  // before the effect commit blanked it).
  const [hidden, setHidden] = useState<boolean>(() => {
    if (!isEnabledByEnv()) return true
    if (isDismissed()) return true
    return false
  })

  if (hidden) return null
  // Route-level exceptions. Each path is documented in the file
  // docstring above; check there before adding a new entry. Matched
  // by exact pathname OR ``base + '/'`` prefix so segment-boundary
  // siblings like ``/privacy-policy`` (NOT ``/privacy``) and
  // ``/student/noticeboard`` (NOT ``/student/notice``) don't get
  // exempted by mistake — raw ``startsWith`` would have. Hash +
  // query params live on ``location.search`` / ``location.hash``
  // (NOT ``pathname``), so e.g. ``/privacy?lang=en#cookies`` sees
  // ``pathname === '/privacy'`` and the exact-match branch fires.
  // CodeRabbit on PR #504 round 1.
  const path = location.pathname
  const isExemptPath = (base: string) =>
    path === base || path.startsWith(`${base}/`)
  const isExemptRoute =
    isExemptPath('/privacy') ||
    isExemptPath('/en/privacy') ||
    isExemptPath('/student/notice') ||
    isExemptPath('/en/student/notice')
  if (isExemptRoute) return null

  const onDismiss = () => {
    setDismissed()
    setHidden(true)
  }

  const isEnglish = lang === 'en'
  const learnMoreHref = isEnglish ? '/privacy?lang=en#cookies' : '/privacy#cookies'

  const message = isEnglish
    ? 'We use only strictly-necessary cookies for login and security. No analytics, no advertising.'
    : '본 사이트는 로그인 및 보안에 꼭 필요한 쿠키만 사용합니다. 분석·광고용 쿠키는 사용하지 않습니다.'
  const learnMoreLabel = isEnglish ? 'Learn more' : '자세히 보기'
  const dismissLabel = isEnglish ? 'Got it' : '확인'

  return (
    <div className={s.cookieNotice} role="region" aria-label={isEnglish ? 'Cookie notice' : '쿠키 안내'}>
      <p className={s.cookieNoticeText}>{message}</p>
      <div className={s.cookieNoticeActions}>
        <Link to={learnMoreHref} className={s.cookieNoticeLink}>
          {learnMoreLabel}
        </Link>
        <button type="button" className={s.cookieNoticeDismiss} onClick={onDismiss}>
          {dismissLabel}
        </button>
      </div>
    </div>
  )
}
